import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Cpu, Trophy, Brain,
  Briefcase, User, ChevronLeft, ChevronRight,
  LogOut, Menu, X, GraduationCap, Heart, Orbit,
  ChevronDown, Diamond, Users as UsersIcon, Network as NetworkIcon, Check,
  Shield, Plug, Mail, History, Cog,
} from 'lucide-react';
import { isAuthenticated, logout, getRole, getMenuAccess, refreshMenuAccess } from './services/auth';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { DepartRouteProvider } from './contexts/DepartRouteContext';
import DepartRouteSwitcher from './components/DepartRouteSwitcher';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import { SkeletonPage } from './components/Skeleton';
import './App.css';

const PIPELINE_COLOR = {
  'cron-9:30am': '#ffd700',
  manual:        '#ffd700',
  watchdog:      '#f59e0b',
  'watchdog-9:45am': '#f59e0b',
  'cron-3pm':    '#3b82f6',
  'cron-9pm':    '#3b82f6',
  'market-sync': '#3b82f6',
};
const PIPELINE_LABEL = {
  'cron-9:30am': 'Daily prediction (9:30 AM)',
  manual:        'Manual re-run',
  watchdog:      'Watchdog (10:00 AM)',
  'watchdog-9:45am': 'Watchdog (10:00 AM)',
  'cron-3pm':    'Market sync (3 PM)',
  'cron-9pm':    'Market sync (9 PM)',
  'market-sync': 'Market sync',
};

// Pages grouped by workspace:
//   pages/retail/   retail user pages (city-scoped)
//   pages/remit/    remit user pages (USD/INR send-or-wait)
//   pages/trade/    trade user pages (XAU/USD spot)
//   pages/admin/    admin-only pages
//   pages/landing/  pre-auth landing + mode chooser
//   pages/Login.jsx + NotFound.jsx — stay at root
const Dashboard    = lazy(() => import('./pages/retail/Dashboard'));
const Engines      = lazy(() => import('./pages/retail/Engines'));
const Performance  = lazy(() => import('./pages/retail/Performance'));
const AIStatus     = lazy(() => import('./pages/retail/AIStatus'));

const RemitDashboard    = lazy(() => import('./pages/remit/Dashboard'));
const DepartDashboard   = lazy(() => import('./pages/depart/Dashboard'));
const DepartPerformance = lazy(() => import('./pages/depart/Performance'));
const DepartEngines     = lazy(() => import('./pages/depart/Engines'));
const DepartSmartEngines = lazy(() => import('./pages/depart/SmartEngines'));
const RemitEngines      = lazy(() => import('./pages/remit/Engines'));
const RemitSmartEngines = lazy(() => import('./pages/remit/SmartEngines'));
const RemitPerformance  = lazy(() => import('./pages/remit/Performance'));

const TradeDashboard   = lazy(() => import('./pages/trade/Dashboard'));
const TradeEngines     = lazy(() => import('./pages/trade/Engines'));
const TradePerformance = lazy(() => import('./pages/trade/Performance'));
const TradeAIML        = lazy(() => import('./pages/trade/AIML'));

const Settings      = lazy(() => import('./pages/admin/Settings'));
const HowItWorks    = lazy(() => import('./pages/admin/HowItWorks'));
const SimulatorPage = lazy(() => import('./pages/admin/Simulator'));
const Backtest      = lazy(() => import('./pages/admin/Backtest'));
const Backfill      = lazy(() => import('./pages/admin/Backfill'));
const Hindsight     = lazy(() => import('./pages/admin/Hindsight'));
const Network       = lazy(() => import('./pages/admin/Network'));
const Health        = lazy(() => import('./pages/admin/Health'));
const Audit         = lazy(() => import('./pages/admin/Audit'));
const ForSon        = lazy(() => import('./pages/admin/ForSon'));
const Users         = lazy(() => import('./pages/admin/Users'));
const Integrations  = lazy(() => import('./pages/admin/Integrations'));
const Alerts        = lazy(() => import('./pages/admin/Alerts'));
const CronHistory   = lazy(() => import('./pages/admin/CronHistory'));
const Garage = lazy(() => import('./pages/admin/Garage'));

// Home page is the public dashboard. The orbital animation that used to live
// at "/" is kept, unchanged, at /cosmic - it is still linked from the header.
const PublicDashboard = lazy(() => import('./pages/landing/PublicDashboard'));
const CosmicLanding = lazy(() => import('./pages/landing/LandingPageMotion'));
const ModeChooser = lazy(() => import('./pages/landing/ModeChooser'));
const Login       = lazy(() => import('./pages/Login'));

const PageLoader = () => <div style={{ padding: 20 }}><SkeletonPage /></div>;

// Cities (markets) — the active gold-trading region whose data the engines
// run against. Add new cities here and their `:city` URL slug will activate.
// `enabled: false` cities show in the switcher as "coming soon".
const CITIES = [
  { id: 'chennai', label: 'Chennai', sub: 'Gold · 22K · INR', enabled: true },
  // { id: 'mumbai',  label: 'Mumbai',  sub: 'Gold · 22K · INR', enabled: false },
];
const DEFAULT_CITY = 'chennai';
const KNOWN_CITY_IDS = CITIES.map(c => c.id);

function isKnownCity(id) { return KNOWN_CITY_IDS.includes(id); }

// Extract the active city from a /retail/:city/... pathname.
// Returns null if the URL has no (or unknown) city segment.
function getCityFromPath(pathname) {
  const parts = pathname.split('/').filter(Boolean); // e.g. ['retail','chennai','dashboard']
  return parts[0] === 'retail' && isKnownCity(parts[1]) ? parts[1] : null;
}

// Drop the city segment so we can match nav items by their base path.
function stripCityFromPath(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'retail' && isKnownCity(parts[1])) {
    return '/retail/' + parts.slice(2).join('/');
  }
  return pathname;
}

// Sidebar nav — items are tagged with roles that may see them.
// `cityScoped: true` items live under /retail/:city/, false items are global
// (settings, how-it-works, etc.) and stay at /retail/page.
const navItems = [
  { to: 'network',     icon: NetworkIcon,     label: 'Network',         end: true, roles: ['admin'],                    cityScoped: false },
  { to: 'dashboard',   icon: LayoutDashboard, label: 'Dashboard',       end: true, roles: ['retail'], cityScoped: true  },
  { to: 'engines',     icon: Cpu,             label: 'Engines',                    roles: ['retail'], cityScoped: true  },
  { to: 'ai',          icon: Brain,           label: 'Smart Engines',             roles: ['retail'], cityScoped: true  },
  { to: 'performance', icon: Trophy,          label: 'Performance',                roles: ['retail'], cityScoped: true  },
  { to: 'remit-dashboard', icon: LayoutDashboard, label: 'Dashboard',   accessKey: 'Remit Dashboard', end: true, roles: ['remit'], cityScoped: false, workspace: 'remit' },
  { to: 'remit/engines',     icon: Cpu,         label: 'Engines',     accessKey: 'Remit Engines',               roles: ['remit'],          cityScoped: false, workspace: 'remit' },
  { to: 'remit/ai',          icon: Brain,       label: 'Smart Engines', accessKey: 'Remit Smart Engines',       roles: ['remit'],          cityScoped: false, workspace: 'remit' },
  { to: 'remit/performance', icon: Trophy,      label: 'Performance', accessKey: 'Remit Performance',           roles: ['remit'],          cityScoped: false, workspace: 'remit' },
  { to: 'depart-dashboard', icon: LayoutDashboard, label: 'Dashboard',  accessKey: 'Depart Dashboard', end: true, roles: ['depart'], cityScoped: false, workspace: 'depart' },
  { to: 'depart/engines',     icon: Cpu,    label: 'Engines',       accessKey: 'Depart Engines',       roles: ['depart'], cityScoped: false, workspace: 'depart' },
  { to: 'depart/ai',          icon: Brain,  label: 'Smart Engines', accessKey: 'Depart Smart Engines', roles: ['depart'], cityScoped: false, workspace: 'depart' },
  { to: 'depart/performance', icon: Trophy, label: 'Performance',   accessKey: 'Depart Performance',   roles: ['depart'], cityScoped: false, workspace: 'depart' },
  { to: 'trade/dashboard',   icon: LayoutDashboard, label: 'Dashboard',   accessKey: 'Trade Dashboard',   end: true, roles: ['trade'], cityScoped: false, workspace: 'trade' },
  { to: 'trade/engines',     icon: Cpu,             label: 'Engines',     accessKey: 'Trade Engines',                roles: ['trade'], cityScoped: false, workspace: 'trade' },
  { to: 'trade/ai',          icon: Brain,           label: 'Smart Engines', accessKey: 'Trade Smart Engines',       roles: ['trade'], cityScoped: false, workspace: 'trade' },
  { to: 'trade/performance', icon: Trophy,          label: 'Performance', accessKey: 'Trade Performance',            roles: ['trade'], cityScoped: false, workspace: 'trade' },
  { to: 'integrations', icon: Plug,           label: 'Integrations',              roles: ['admin'],          cityScoped: false },
  { to: 'alerts',       icon: Mail,           label: 'Alerts',                    roles: ['admin'],          cityScoped: false },
  { to: 'audit',        icon: Shield,         label: 'Audit Trails',              roles: ['admin'],          cityScoped: false },
  { to: 'cron-history', icon: History,        label: 'Cron History',              roles: ['admin'],          cityScoped: false },
  { to: 'users',        icon: UsersIcon,      label: 'Users',                     roles: ['admin'],          cityScoped: false },
  { to: 'garage',       icon: Cog,            label: 'Engine Garage',             roles: ['admin'],          cityScoped: false, dividerAbove: true, multiWorkspace: true },
  { to: 'settings',    icon: Briefcase,       label: 'Workspace',                  roles: ['admin'],          cityScoped: false, multiWorkspace: true },
];

// Items rendered under the profile dropdown (not in sidebar nav).
const profileMenuItems = [];

// Topbar tools — city-scoped so per-city history stays per city.
const topbarTools = [
  { to: 'simulator',    icon: Orbit,         label: 'Simulator',     roles: ['admin'], cityScoped: false },
  { to: 'how-it-works', icon: GraduationCap, label: 'How It Works',  roles: ['admin'], cityScoped: false },
];

// Legacy label aliases — older menu_access rows may have used short names
// before some items were renamed. Keep this small map so existing data
// keeps working without forcing every user to re-save their access rights.
const LABEL_ALIAS = {
  'Engine Garage': ['Garage'],
  // "AI / ML" pages were renamed "Smart Engines" — menu_access rows saved
  // before the rename still carry the old label. Without these aliases the
  // sidebar item silently vanishes for any user with customised access
  // (observed: admin granted "AI / ML" + "Trade AI / ML", saw neither menu).
  'Smart Engines': ['AI / ML', 'AI/ML'],
  'Trade Smart Engines': ['Trade AI / ML', 'Trade AI/ML'],
  // Pro → Remit concept change (2026-07-10): rows saved before migration 051
  'Remit Dashboard': ['Pro Dashboard', 'Ask Genie'],
  'Remit Performance': ['Pro Performance', 'Daily Picks'],
};

function visibleFor(items, role, menuAccess) {
  // Per-user menu_access override (admin-edited list) takes precedence.
  // null/undefined means "use role-based defaults".
  if (Array.isArray(menuAccess)) {
    return items.filter(it => {
      const key = it.accessKey || it.label;
      if (menuAccess.includes(key)) return true;
      const aliases = LABEL_ALIAS[key];
      return aliases ? aliases.some(a => menuAccess.includes(a)) : false;
    });
  }
  return items.filter(it => !it.roles || it.roles.includes(role));
}

// Resolve a nav item's final URL given the active city.
// City-scoped items live under /retail/:city/page. Global items
// (ask-genie, simulator, settings, etc.) sit at the top level: /page.
function resolveNavTo(item, city) {
  return item.cityScoped ? `/retail/${city}/${item.to}` : `/${item.to}`;
}

function ProtectedRoute({ children }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return children;
}

// Where each role should land after login / when they hit a page they can't see.
// Remit users land on their signal dashboard directly.
function defaultLandingFor(role) {
  if (role === 'admin') return '/network';
  if (role === 'remit') return '/remit-dashboard';
  if (role === 'depart') return '/depart-dashboard';
  if (role === 'trade') return '/trade/dashboard';
  return `/retail/${DEFAULT_CITY}/dashboard`;
}

// Topbar-styled city switcher — compact pill that hangs a dropdown below.
// Lives in the topbar to the left of the global tool icons (How It Works).
function TopbarCitySwitcher() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentCityId = getCityFromPath(location.pathname) || DEFAULT_CITY;
  const activeCity = CITIES.find(c => c.id === currentCityId) || CITIES[0];

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchCity = (nextId) => {
    setOpen(false);
    if (nextId === currentCityId) return;
    const fromCity = getCityFromPath(location.pathname);
    const newPath = fromCity
      ? location.pathname.replace(`/retail/${fromCity}/`, `/retail/${nextId}/`)
      : `/retail/${nextId}/dashboard`;
    navigate(newPath);
  };

  // City scoping only applies to retail pages — hide the switcher everywhere
  // else (remit, trade, admin) so the topbar doesn't suggest a city context.
  if (!getCityFromPath(location.pathname)) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Responsive: on phones hide the label + chevron, keep only the icon
          badge — and anchor the dropdown panel to the LEFT of the trigger so
          it opens into the screen instead of overflowing past the left edge. */}
      <style>{`
        @media (max-width: 640px) {
          .city-switcher-btn { padding: 5px 10px 5px 5px !important; gap: 6px !important; }
          .city-switcher-chev { display: none !important; }
          .city-switcher-icon { width: 24px !important; height: 24px !important; }
          .city-switcher-icon svg { width: 12px !important; height: 12px !important; }
          .city-switcher-panel { left: 0 !important; right: auto !important; }
          .city-switcher-caret { right: auto !important; left: 24px !important; }
        }
      `}</style>

      {/* Trigger pill */}
      <button
        type="button"
        className="city-switcher-btn"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={activeCity.label}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: open
            ? 'linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,140,0,0.10))'
            : 'linear-gradient(135deg, #14142a, #0f0f23)',
          border: `1px solid ${open ? '#ffd70080' : '#2a2a4a'}`,
          borderRadius: 999,
          padding: '7px 12px 7px 8px',
          color: '#fff',
          fontSize: 12, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          letterSpacing: 0.2,
          transition: 'background 150ms, border-color 150ms, box-shadow 150ms',
          boxShadow: open ? '0 0 0 3px rgba(255,215,0,0.12)' : 'none',
        }}
      >
        {/* Icon badge — always visible */}
        <span className="city-switcher-icon" style={{
          width: 22, height: 22, borderRadius: '50%',
          background: 'rgba(255,215,0,0.18)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Diamond size={12} style={{ color: '#ffd700' }} />
        </span>
        <span className="city-switcher-label">{activeCity.label}</span>
        <ChevronDown
          className="city-switcher-chev"
          size={13}
          style={{
            color: open ? '#ffd700' : '#888',
            transition: 'transform 200ms ease, color 150ms',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <>
          {/* Small caret pointing up to the trigger */}
          <span className="city-switcher-caret" style={{
            position: 'absolute', top: 'calc(100% + 1px)', right: 16,
            width: 10, height: 10, background: '#13132a',
            borderTop: '1px solid #2a2a4a', borderLeft: '1px solid #2a2a4a',
            transform: 'rotate(45deg)',
            zIndex: 501,
          }} />
          <div
            role="listbox"
            className="city-switcher-panel"
            style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              minWidth: 260,
              maxWidth: 'calc(100vw - 24px)',
              background: '#13132a',
              border: '1px solid #2a2a4a',
              borderRadius: 12,
              padding: 6,
              boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
              zIndex: 500,
              overflow: 'hidden',
            }}
          >
            <div style={{
              color: '#666', fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 1.2,
              padding: '8px 10px 6px',
            }}>
              Switch market
            </div>
            {CITIES.map(c => {
              const isActive = c.id === activeCity.id;
              const disabled = c.enabled === false;
              return (
                <button
                  key={c.id}
                  role="option"
                  aria-selected={isActive}
                  disabled={disabled}
                  onClick={() => switchCity(c.id)}
                  onMouseEnter={(e) => { if (!disabled && !isActive) e.currentTarget.style.background = '#1a1a30'; }}
                  onMouseLeave={(e) => { if (!disabled && !isActive) e.currentTarget.style.background = 'transparent'; }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    width: '100%', padding: '10px 12px',
                    background: isActive ? 'rgba(255, 215, 0, 0.10)' : 'transparent',
                    border: isActive ? '1px solid #ffd70040' : '1px solid transparent',
                    borderRadius: 9,
                    color: isActive ? '#fff' : '#ddd',
                    fontSize: 13,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    marginBottom: 2,
                    transition: 'background 120ms, border-color 120ms',
                  }}
                >
                  {/* Icon badge */}
                  <span style={{
                    width: 30, height: 30, borderRadius: 9,
                    background: isActive ? 'rgba(255,215,0,0.18)' : '#0a0a1e',
                    border: `1px solid ${isActive ? '#ffd70060' : '#2a2a4a'}`,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Diamond size={13} style={{ color: isActive ? '#ffd700' : '#666' }} />
                  </span>

                  {/* Label block */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: isActive ? '#ffd700' : '#eee' }}>
                      {c.label}
                    </div>
                    <div style={{ fontSize: 10, color: '#666', marginTop: 2, lineHeight: 1.3 }}>
                      {c.sub}
                    </div>
                  </div>

                  {/* Right-side state indicator */}
                  {disabled ? (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: '#888',
                      background: '#1a1a2e', border: '1px solid #2a2a4a',
                      padding: '3px 8px', borderRadius: 999,
                      textTransform: 'uppercase', letterSpacing: 0.5,
                      flexShrink: 0,
                    }}>Soon</span>
                  ) : isActive ? (
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%',
                      background: '#ffd700',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Check size={11} strokeWidth={3} color="#000" />
                    </span>
                  ) : (
                    <span style={{
                      width: 14, height: 14, borderRadius: '50%',
                      border: '1.5px solid #2a2a4a',
                      flexShrink: 0,
                    }} />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Accordion-mode collapse for the admin sidebar: at most ONE role-group is
  // expanded at a time. Clicking a header expands it and collapses the others;
  // clicking the open one closes it (all groups collapsed). Persisted in
  // localStorage. Default: 'admin' (first group expanded). Only takes effect
  // when role-group headers render, which only happens for admin users.
  const [expandedGroup, setExpandedGroup] = useState(() => {
    try {
      const v = localStorage.getItem('sidebar.expandedGroup');
      return v === null ? 'admin' : (v || null); // null = all collapsed
    } catch { return 'admin'; }
  });
  const toggleGroup = (groupRole) => {
    setExpandedGroup(prev => {
      const next = prev === groupRole ? null : groupRole;
      try { localStorage.setItem('sidebar.expandedGroup', next === null ? '' : next); } catch {}
      return next;
    });
  };
  const [profileOpen, setProfileOpen] = useState(false);
  const [pipelineStatus, setPipelineStatus] = useState({ running: false, name: null });
  const [pipelineDropdownOpen, setPipelineDropdownOpen] = useState(false);
  const profileRef = useRef(null);
  const pipelineRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const role = getRole() || 'retail';
  const [menuAccess, setMenuAccessState] = useState(() => getMenuAccess());
  const isAdmin = role === 'admin';
  const visibleNav = visibleFor(navItems, role, menuAccess);
  const visibleTools = visibleFor(topbarTools, role, menuAccess);

  // Refresh menu access from DB on boot (covers admin self-edits since last login)
  // and listen for in-window updates triggered after saving access rights.
  useEffect(() => {
    refreshMenuAccess().then(fresh => { if (fresh !== undefined) setMenuAccessState(getMenuAccess()); });
    const onChange = () => setMenuAccessState(getMenuAccess());
    window.addEventListener('menuAccessChanged', onChange);
    return () => window.removeEventListener('menuAccessChanged', onChange);
  }, []);

  useEffect(() => { setMobileOpen(false); setProfileOpen(false); setPipelineDropdownOpen(false); }, [location.pathname]);
  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (pipelineRef.current && !pipelineRef.current.contains(e.target)) setPipelineDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  useEffect(() => {
    if (!isAdmin) return;
    const { api } = require('./services/api');
    const poll = setInterval(async () => {
      try {
        const s = await api.getPipelineStatus();
        setPipelineStatus(s);
      } catch { /* ignore — backend may be starting up */ }
    }, 5000);
    return () => clearInterval(poll);
  }, [isAdmin]);

  const currentCity = getCityFromPath(location.pathname) || DEFAULT_CITY;

  // Strip the city segment so topbar-tool active-state matching works for
  // both /retail/:city/... and global paths.
  const normPath = stripCityFromPath(location.pathname);

  async function handleSignOut() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="app-layout">
      <div className="cave-bg">
        <div className="smoke smoke-1" />
        <div className="smoke smoke-2" />
        <div className="smoke smoke-3" />
        <div className="particles">
          {[...Array(8)].map((_, i) => <div key={i} className="particle" />)}
        </div>
      </div>

      {mobileOpen && (
        <button
          type="button"
          className="sidebar-overlay"
          aria-label="Close navigation menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`} aria-label="Primary">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img src="/logo.png" alt="" width={22} height={22} className="brand-icon" aria-hidden="true" />
            {!collapsed && <span className="brand-text">GenieAI</span>}
          </div>
          <button
            className="collapse-btn desktop-only"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronLeft size={16} aria-hidden="true" />}
          </button>
          <button
            className="collapse-btn mobile-only"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation menu"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* City switcher moved to topbar — keep this slot empty to preserve
            the existing top spacing in the sidebar header. */}

        <nav className="sidebar-nav">
          {(() => {
            // Group items by their primary role so cross-role grants from the
            // admin's user-edit panel render as labelled sections instead of a
            // mixed flat list. Order: admin first, then retail, trade, pro.
            const ROLE_ORDER = ['admin', 'retail', 'remit', 'depart', 'trade'];
            const ROLE_LABEL = { admin: 'Admin', retail: 'Retail', trade: 'Trade', remit: 'Remit', depart: 'Depart' };
            const ROLE_COLOR = { admin: '#ffd700', retail: '#3b82f6', trade: '#a855f7', remit: '#22c55e', depart: '#f97316' };
            const groups = new Map();
            for (const it of visibleNav) {
              const primaryRole = (it.roles && it.roles[0]) || 'admin';
              if (!groups.has(primaryRole)) groups.set(primaryRole, []);
              groups.get(primaryRole).push(it);
            }
            const orderedRoles = ROLE_ORDER.filter(r => groups.has(r));
            const showHeaders = orderedRoles.length > 1; // only show role headers when admin has cross-role grants
            return orderedRoles.map((groupRole, gi) => {
              const isGroupCollapsed = expandedGroup !== groupRole;
              return (
              <React.Fragment key={groupRole}>
                {showHeaders && !collapsed && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupRole)}
                    aria-expanded={!isGroupCollapsed}
                    aria-controls={`sidebar-group-${groupRole}`}
                    title={isGroupCollapsed ? `Expand ${ROLE_LABEL[groupRole]}` : `Collapse ${ROLE_LABEL[groupRole]}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: 'calc(100% - 16px)',
                      margin: gi === 0 ? '6px 8px 4px' : '14px 8px 4px',
                      padding: '2px 4px',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                    }}
                  >
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: `${ROLE_COLOR[groupRole]}1a`,
                      border: `1px solid ${ROLE_COLOR[groupRole]}40`,
                      color: ROLE_COLOR[groupRole],
                      padding: '2px 9px', borderRadius: 999,
                      fontSize: 9, fontWeight: 800,
                      textTransform: 'uppercase', letterSpacing: 1.2,
                    }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: ROLE_COLOR[groupRole], boxShadow: `0 0 4px ${ROLE_COLOR[groupRole]}` }} />
                      {ROLE_LABEL[groupRole]}
                    </span>
                    <ChevronDown
                      size={14}
                      color={ROLE_COLOR[groupRole]}
                      style={{
                        transition: 'transform 0.18s ease',
                        transform: isGroupCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        opacity: 0.7,
                      }}
                    />
                  </button>
                )}
                {showHeaders && collapsed && gi > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }} aria-hidden="true">
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: ROLE_COLOR[groupRole], boxShadow: `0 0 4px ${ROLE_COLOR[groupRole]}` }} />
                  </div>
                )}
                {/* When the sidebar itself is collapsed (narrow rail mode), per-group
                    collapse is ignored — icons must stay visible. When sidebar is
                    expanded, hide this group's items if the user collapsed it. */}
                {showHeaders && !collapsed && isGroupCollapsed ? null : (
                <div id={`sidebar-group-${groupRole}`}>
                {groups.get(groupRole).map(item => (
                  <React.Fragment key={item.to}>
                    {item.dividerAbove && !showHeaders && (
                      <div style={{ height: 1, background: '#1e1e3a', margin: '10px 12px' }} aria-hidden="true" />
                    )}
                    <NavLink
                      to={resolveNavTo(item, currentCity)}
                      end={item.end}
                      className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                      style={showHeaders && !collapsed ? { paddingLeft: 22 } : undefined}
                      title={collapsed
                        ? `${item.label}${item.multiWorkspace ? ' · Retail · Pro · Trade' : ''}`
                        : item.multiWorkspace ? 'Retail · Pro · Trade workspaces' : undefined}
                    >
                      <item.icon size={18} className="sidebar-link-icon" />
                      {!collapsed && <span className="sidebar-link-text">{item.label}</span>}
                      {item.multiWorkspace && (
                        <span style={{
                          marginLeft: collapsed ? 0 : 'auto',
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          flexShrink: 0,
                        }} aria-label="Retail, Pro and Trade workspaces">
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#3b82f6', boxShadow: '0 0 3px #3b82f680' }} />
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 3px #22c55e80' }} />
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#a855f7', boxShadow: '0 0 3px #a855f780' }} />
                        </span>
                      )}
                    </NavLink>
                  </React.Fragment>
                ))}
                </div>
                )}
              </React.Fragment>
              );
            });
          })()}
        </nav>

        <div className="sidebar-footer">
          {!collapsed && (
            <div className="sidebar-version">
              <span>Powered by CodeSaha</span>
            </div>
          )}
        </div>
      </aside>

      <div className={`main-area ${collapsed ? 'expanded' : ''}`}>
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="mobile-menu-btn mobile-only"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={mobileOpen}
            >
              <Menu size={20} aria-hidden="true" />
            </button>
          </div>
          <div className="topbar-right">
            {/* Pipeline indicator — visible only while a cron/pipeline is running */}
            {pipelineStatus.running && (
              <div ref={pipelineRef} style={{ position: 'relative', marginRight: 6 }}>
                <button
                  onClick={() => setPipelineDropdownOpen(o => !o)}
                  title={pipelineStatus.name || 'Pipeline running'}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: PIPELINE_COLOR[pipelineStatus.name] ? PIPELINE_COLOR[pipelineStatus.name] + '20' : '#ffd70020', border: `1px solid ${PIPELINE_COLOR[pipelineStatus.name] || '#ffd700'}40`, cursor: 'pointer' }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: PIPELINE_COLOR[pipelineStatus.name] || '#ffd700', display: 'inline-block', animation: 'pipeline-blink 1s ease-in-out infinite' }} />
                </button>
                {pipelineDropdownOpen && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 180, background: '#0f0f23', border: '1px solid #1e1e3a', borderRadius: 10, boxShadow: '0 8px 24px #00000060', zIndex: 9999, padding: '10px 14px' }}>
                    <div style={{ color: PIPELINE_COLOR[pipelineStatus.name] || '#ffd700', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                      {PIPELINE_LABEL[pipelineStatus.name] || pipelineStatus.name || 'Pipeline'}
                    </div>
                    <div style={{ color: '#555', fontSize: 10 }}>Running now…</div>
                  </div>
                )}
              </div>
            )}
            {/* City switcher — only shown to roles that see city-scoped pages. */}
            {visibleNav.some(item => item.cityScoped) && <TopbarCitySwitcher />}
            {/* One route selection governs the whole Depart section, the way the
                city switcher governs retail. Only shown on depart pages. */}
            {location.pathname.startsWith('/depart') && <DepartRouteSwitcher />}

            {/* Global tools */}
            {visibleTools.map(tool => {
              const toolPath = resolveNavTo(tool, currentCity);
              const targetForMatch = tool.cityScoped ? `/retail/${tool.to}` : `/${tool.to}`;
              const isActive = normPath.startsWith(targetForMatch);
              return (
                <button
                  key={tool.to}
                  className={`topbar-icon-btn ${isActive ? 'active' : ''}`}
                  onClick={() => navigate(toolPath)}
                  title={tool.label}
                  aria-label={tool.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <tool.icon size={22} aria-hidden="true" />
                </button>
              );
            })}

            {(isAdmin || visibleTools.length > 0) && <div className="topbar-divider" />}

            {isAdmin && (
              <button
                className="topbar-icon-btn heart-btn"
                onClick={() => navigate('/letter')}
                title="For My Son"
                aria-label="Letter for my son"
              >
                <span className="heart-box">
                  <span className="heart-beat"><Heart size={16} fill="#e05a7a" stroke="#e05a7a" aria-hidden="true" /></span>
                </span>
              </button>
            )}

            <div className="profile-wrapper" ref={profileRef}>
              <button
                className="profile-badge"
                onClick={() => setProfileOpen(!profileOpen)}
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                aria-label="Account menu"
              >
                <div className="profile-avatar">
                  <User size={16} aria-hidden="true" />
                </div>
                <span className="profile-name desktop-only">{role.charAt(0).toUpperCase() + role.slice(1)}</span>
              </button>
              {profileOpen && (
                <div className="profile-dropdown" role="menu">
                  {visibleFor(profileMenuItems, role, menuAccess).map(item => (
                    <button
                      key={item.to}
                      className="profile-dropdown-item"
                      onClick={() => { setProfileOpen(false); navigate(resolveNavTo(item, currentCity)); }}
                      role="menuitem"
                    >
                      <item.icon size={14} aria-hidden="true" /> {item.label}
                    </button>
                  ))}
                  <button className="profile-dropdown-item danger" onClick={handleSignOut} role="menuitem">
                    <LogOut size={14} aria-hidden="true" /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="page-content" id="main-content" tabIndex={-1}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Retail workspace — city-scoped pages live under /retail/:city/. */}
              <Route path="retail/:city/dashboard"   element={<ErrorBoundary>{['admin','retail'].includes(role) ? <Dashboard />   : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="retail/:city/engines"     element={<ErrorBoundary>{['admin','retail'].includes(role) ? <Engines />     : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="retail/:city/performance" element={<ErrorBoundary>{['admin','retail'].includes(role) ? <Performance /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="retail/:city/ai"          element={<ErrorBoundary>{['admin','retail'].includes(role) ? <AIStatus />    : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />

              {/* Trade workspace — flat routes, no city. */}
              <Route path="trade/dashboard"   element={<ErrorBoundary>{['admin','trade'].includes(role) ? <TradeDashboard />   : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="trade/engines"     element={<ErrorBoundary>{['admin','trade'].includes(role) ? <TradeEngines />     : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="trade/performance" element={<ErrorBoundary>{['admin','trade'].includes(role) ? <TradePerformance /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="trade/ai"          element={<ErrorBoundary>{['admin','trade'].includes(role) ? <TradeAIML />        : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />

              {/* Global tools — not city-specific. URLs sit at top level. */}
              <Route path="network"      element={<ErrorBoundary>{isAdmin ? <Network /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="hindsight"    element={<ErrorBoundary>{isAdmin ? <Hindsight /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="health"       element={<ErrorBoundary>{isAdmin ? <Health />    : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="audit"        element={<ErrorBoundary>{isAdmin ? <Audit />     : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="integrations" element={<ErrorBoundary>{isAdmin ? <Integrations /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="alerts"       element={<ErrorBoundary>{isAdmin ? <Alerts />       : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="cron-history" element={<ErrorBoundary>{isAdmin ? <CronHistory />  : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="garage" element={<ErrorBoundary>{isAdmin ? <Garage /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="backtest"     element={<ErrorBoundary>{isAdmin ? <Backtest />  : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="backfill"     element={<ErrorBoundary>{isAdmin ? <Backfill />  : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="remit-dashboard" element={<ErrorBoundary>{['admin','remit'].includes(role) ? <RemitDashboard /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="depart-dashboard" element={<ErrorBoundary>{['admin','depart'].includes(role) ? <DepartDashboard /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="depart/engines" element={<ErrorBoundary>{['admin','depart'].includes(role) ? <DepartEngines /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="depart/ai" element={<ErrorBoundary>{['admin','depart'].includes(role) ? <DepartSmartEngines /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="depart/performance" element={<ErrorBoundary>{['admin','depart'].includes(role) ? <DepartPerformance /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="remit/engines" element={<ErrorBoundary>{['admin','remit'].includes(role) ? <RemitEngines /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="remit/ai" element={<ErrorBoundary>{['admin','remit'].includes(role) ? <RemitSmartEngines /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="remit/performance" element={<ErrorBoundary>{['admin','remit'].includes(role) ? <RemitPerformance /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="simulator"    element={<ErrorBoundary>{isAdmin ? <SimulatorPage /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="how-it-works" element={<ErrorBoundary>{isAdmin ? <HowItWorks /> : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="settings"     element={<ErrorBoundary>{isAdmin ? <Settings />   : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="letter"       element={<ErrorBoundary>{isAdmin ? <ForSon />     : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />
              <Route path="users"        element={<ErrorBoundary>{isAdmin ? <Users />      : <Navigate to={defaultLandingFor(role)} replace />}</ErrorBoundary>} />

              {/* Catch-all → role-appropriate landing */}
              <Route path="*" element={<Navigate to={defaultLandingFor(role)} replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
    <ErrorBoundary>
    <Router>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<PublicDashboard />} />
        {/* One public address per desk, so each is linkable and the back button
            moves between them. Declared before the protected "/*" catch-all so
            they resolve for signed-out visitors; the authed app's own desk
            routes are deeper (retail/:city/dashboard, remit/engines) and are
            unaffected. */}
        <Route path="/retail" element={<PublicDashboard />} />
        <Route path="/remit" element={<PublicDashboard />} />
        <Route path="/trade" element={<PublicDashboard />} />
        <Route path="/depart" element={<PublicDashboard />} />
        <Route path="/cosmic" element={<CosmicLanding />} />
        <Route path="/login" element={<Login />} />
        <Route path="/choose" element={
          <ProtectedRoute>
            <ModeChooser />
          </ProtectedRoute>
        } />
        {/* Everything else (including /retail/:city/... and global tools)
            renders through AdminLayout so the sidebar/topbar stay consistent. */}
        <Route path="/*" element={
          <ProtectedRoute>
            <WorkspaceProvider><DepartRouteProvider>
              <AdminLayout />
            </DepartRouteProvider></WorkspaceProvider>
          </ProtectedRoute>
        } />
      </Routes>
      </Suspense>
    </Router>
    </ErrorBoundary>
    </ToastProvider>
  );
}

export default App;
